<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import HomeBadge from './MobileMenuBarV2/HomeBadge.vue';
import LoginBadge from './MobileMenuBarV2/LoginBadge.vue';
import FidelidadeBadge from './MobileMenuBarV2/FidelidadeBadge.vue';
import MenuItem from './MobileMenuBarV2/MenuItem.vue';
import { useMobileMenu } from '~/composables/useMobileMenu';

const layoutStore = useLayoutStore();
const { isMenuAnimating } = storeToRefs(layoutStore);
const casinoStore = useCasinoStore();
const { isLayoutMobileOrTablet } = useLayout();
const { userCanOnlyWithdraw } = useUserLimits();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { mainMenuButtons } = useMobileMenu();
const { isClubFidelidadeEnabled } = useFeatureFlagsJson();

const isMobileMenuBarVisible = computed(() => {
  return isLayoutMobileOrTablet.value;
});

const bottomMenu = ref(null);

const { height: bottomMenuHeight } = useElementSize(
  bottomMenu,
  { height: 0, width: 0 },
  { box: 'border-box' },
);

watch(bottomMenuHeight, (newHeight) => {
  layoutStore.setBottomMenuHeight(newHeight);
});

// iOS Safari: esconde o menu inferior quando o teclado virtual abre — senão ele
// aparece cortado por cima do teclado. Detecta pelo encolhimento da
// visualViewport: o teclado reduz ~250–350px; a toolbar do Safari só ~44–50px.
// Usa `innerHeight - height` (SEM offsetTop, que oscila durante o scroll) com
// limiar de 150px pra não dar falso-positivo no meio do scroll.
const isKeyboardOpen = ref(false);
const KEYBOARD_INSET_THRESHOLD = 150;
let keyboardRaf = 0;

function updateKeyboardState() {
  const vv = window.visualViewport;

  if (!vv) {
    return;
  }

  isKeyboardOpen.value =
    window.innerHeight - vv.height > KEYBOARD_INSET_THRESHOLD;
}

function scheduleKeyboardUpdate() {
  if (keyboardRaf) {
    return;
  }

  keyboardRaf = requestAnimationFrame(() => {
    keyboardRaf = 0;
    updateKeyboardState();
  });
}

onMounted(() => {
  if (typeof window === 'undefined' || !window.visualViewport) {
    return;
  }

  updateKeyboardState();
  window.visualViewport.addEventListener('resize', scheduleKeyboardUpdate);
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined' || !window.visualViewport) {
    return;
  }

  window.visualViewport.removeEventListener('resize', scheduleKeyboardUpdate);

  if (keyboardRaf) {
    cancelAnimationFrame(keyboardRaf);
  }
});
</script>

<template>
  <div
    v-if="isMobileMenuBarVisible"
    id="divMobileMenu"
    ref="bottomMenu"
    class="bar-wrapper"
    :class="{ hidden: casinoStore.isGameModalOpen || isKeyboardOpen }"
  >
    <div
      v-if="!userCanOnlyWithdraw"
      class="bar-bg"
    >
      <!-- Circular bump behind center badge -->
      <div class="center-bump" />

      <!-- Main bar surface -->
      <div class="bar-surface">
        <div class="menu-items">
          <template
            v-for="(button, index) in mainMenuButtons"
            :key="index"
          >
            <!-- Center slot (index 2). Com o Clube de Fidelidade ligado, só o
                 estado LOGADO muda: vira o FidelidadeBadge (abre o hub) no lugar
                 da casinha. Deslogado segue igual (LoginBadge → login), com ou
                 sem a flag. -->
            <template
              v-if="index === 2 && isClubFidelidadeEnabled && isAuthenticated"
            >
              <FidelidadeBadge />
            </template>

            <template v-else-if="index === 2 && isAuthenticated">
              <HomeBadge />
            </template>

            <LoginBadge
              v-else-if="index === 2"
              :on-click="button.onClick"
            />

            <MenuItem
              v-else
              :button="button"
              :disabled="isMenuAnimating && index === 4"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bar-wrapper {
  position: fixed;
  bottom: -1px;
  left: 0;
  right: 0;
  z-index: 70;
  @apply bg-bg-primary;
  border-radius: 8.84px 8.84px 0 0;
}

// Outer container that holds both the bump and the bar
.bar-bg {
  position: relative;
  width: 100%;
  // Total height = bump protrusion (23px) + bar (66px) — but bump overlaps bar top
  height: 66px;
  @apply bg-bg-primary;
}

// Grey circular bump that peeks above the bar — matches Figma "Ellipse 49"
// Positioned centered, top: 3px relative to bar-bg, 74x71px
.center-bump {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 74px;
  height: 74px;
  border-radius: 50%;
  @apply bg-bg-primary;
}

// The actual bar surface — matches Figma "Rectangle 1"
// rounded top corners, gradient + blur
.bar-surface {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 66px;
  border-radius: 8.84px 8.84px 0 0;
}

.menu-items {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 100%;
  padding: 0 10px 8px;
  gap: 8px;
}
</style>
